import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Select, Empty, Skeleton } from "antd";
import {
  getActiveCampaigns,
  getCampaignInsights,
} from "../features/customers/customerSlice";

const Dashboard = () => {
  const dispatch = useDispatch();

  const {
    activeCampaigns,
    campaignInsights,
    activeCampaignsLoading,
    campaignInsightsLoading,
  } = useSelector((state) => state.customer);

  console.log(campaignInsightsLoading,"loading")


  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  useEffect(() => {
    dispatch(getActiveCampaigns());
    dispatch(getCampaignInsights());
  }, [dispatch]);

  const formatDate = (date) => {
    if (!date) return "—";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatObjective = (objective) => {
    const objectiveMap = {
      OUTCOME_LEADS: "Leads",
      OUTCOME_SALES: "Sales",
      OUTCOME_TRAFFIC: "Traffic",
      OUTCOME_ENGAGEMENT: "Engagement",
      OUTCOME_AWARENESS: "Awareness",
      OUTCOME_APP_PROMOTION: "App Promotion",
    };

    return objectiveMap[objective] || objective || "—";
  };

  const groupedInsights = useMemo(() => {
    if (!Array.isArray(campaignInsights)) return [];

    const grouped = {};

    campaignInsights.forEach((item, index) => {
      const scheduleStart = item.schedule_start || item.date_start || null;
      const scheduleEnd = item.schedule_end || item.date_stop || null;

      const key = [
        item.campaign_name || "Unnamed Campaign",
        item.adset_name || "Unnamed Ad Set",
        scheduleStart || "no-start",
        scheduleEnd || "no-end",
      ].join("|");

      if (!grouped[key]) {
        grouped[key] = {
          key,
          campaign_name: item.campaign_name || "Unnamed Campaign",
          adset_name: item.adset_name || "Unnamed Ad Set",
          schedule_start: scheduleStart,
          schedule_end: scheduleEnd,
          spend: 0,
          clicks: 0,
          whatsapp_clicks: 0,
          impressions: 0,
          _index: index,
        };
      }

      grouped[key].spend += Number(item.spend || 0);
      grouped[key].clicks += Number(item.clicks || 0);
      grouped[key].whatsapp_clicks += Number(item.whatsapp_clicks || 0);
      grouped[key].impressions += Number(item.impressions || 0);
    });

    return Object.values(grouped);
  }, [campaignInsights]);

  const filteredInsights = useMemo(() => {
    return groupedInsights.filter((item) => {
      if (!item.schedule_start) return false;

      const startDate = new Date(item.schedule_start);
      const endDate = item.schedule_end
        ? new Date(item.schedule_end)
        : startDate;

      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();

      const yearMatch =
        selectedYear === "all" ||
        (selectedYear >= startYear && selectedYear <= endYear);

      if (!yearMatch) return false;

      if (selectedMonth === "all") return true;

      const startMonth = startDate.getMonth();
      const endMonth = endDate.getMonth();

      if (startYear === endYear) {
        return (
          selectedYear === startYear &&
          selectedMonth >= startMonth &&
          selectedMonth <= endMonth
        );
      }

      if (selectedYear === startYear) {
        return selectedMonth >= startMonth;
      }

      if (selectedYear === endYear) {
        return selectedMonth <= endMonth;
      }

      return selectedYear > startYear && selectedYear < endYear;
    });
  }, [groupedInsights, selectedYear, selectedMonth]);

  const hasInsightsData =
    Array.isArray(filteredInsights) && filteredInsights.length > 0;

  const totalSpend = filteredInsights.reduce(
    (sum, item) => sum + Number(item.spend || 0),
    0
  );

  const totalClicks = filteredInsights.reduce(
    (sum, item) => sum + Number(item.clicks || 0),
    0
  );

  const totalWhatsappClicks = filteredInsights.reduce(
    (sum, item) => sum + Number(item.whatsapp_clicks || 0),
    0
  );

  const totalImpressions = filteredInsights.reduce(
    (sum, item) => sum + Number(item.impressions || 0),
    0
  );

  const displayCurrency = (value) => {
    if (!hasInsightsData) return "—";
    return `KES ${Number(value || 0).toLocaleString()}`;
  };

  const displayNumber = (value) => {
    if (!hasInsightsData) return "—";
    return Number(value || 0).toLocaleString();
  };

  const columns = [
    {
      title: "Campaign",
      dataIndex: "campaign_name",
      key: "campaign_name",
    },
    {
      title: "Schedule",
      key: "schedule",
      render: (_, record) => {
        const start = formatDate(record.schedule_start);
        const end = record.schedule_end
          ? formatDate(record.schedule_end)
          : "Ongoing";
        return `${start} - ${end}`;
      },
    },
    {
      title: "Clicks",
      dataIndex: "clicks",
      sorter: (a, b) => Number(a.clicks || 0) - Number(b.clicks || 0),
    },
    {
      title: "WhatsApp Clicks",
      dataIndex: "whatsapp_clicks",
      sorter: (a, b) =>
        Number(a.whatsapp_clicks || 0) - Number(b.whatsapp_clicks || 0),
    },
    {
      title: "Spend (KES)",
      dataIndex: "spend",
      sorter: (a, b) => Number(a.spend || 0) - Number(b.spend || 0),
      render: (value) => `KES ${Number(value || 0).toLocaleString()}`,
    },
    {
      title: "Impressions",
      dataIndex: "impressions",
      sorter: (a, b) =>
        Number(a.impressions || 0) - Number(b.impressions || 0),
    },
  ];

  const dataSource = filteredInsights.map((item, index) => ({
    key: item.key || `${item.campaign_name || "campaign"}-${index}`,
    ...item,
  }));

  const years = [
    { value: "all", label: "All Years" },
    { value: currentYear, label: currentYear },
    { value: currentYear - 1, label: currentYear - 1 },
  ];

  const months = [
    { value: "all", label: "All Months" },
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  const renderStatSkeletons = () => (
    <div className="row mb-4 g-3">
      {[1, 2, 3, 4].map((item) => (
        <div className="col-md-3" key={item}>
          <div className="bg-white p-4 rounded shadow-sm">
            <Skeleton.Input
              active
              size="small"
              style={{ width: 100, marginBottom: 12, height:15 }}
            />
            <Skeleton.Input active size="default" style={{ width: 130, height:25 }} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderCampaignSkeletons = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginBottom: 20,
      }}
    >
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="bg-white rounded shadow-sm"
          style={{
            width: "100%",
            border: "1px solid #f0f0f0",
            padding: "18px 20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 240, flex: "1 1 260px" }}>
              <Skeleton.Input
                active
                style={{ width: "70%", marginBottom: 10 }}
              />
              <Skeleton.Input
                active
                size="small"
                style={{ width: "45%" }}
              />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "stretch",
                gap: 12,
                flexWrap: "wrap",
                flex: "3 1 700px",
              }}
            >
              {[1, 2, 3, 4].map((box) => (
                <div
                  key={box}
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <Skeleton.Input
                    active
                    size="small"
                    style={{ width: "60%", marginBottom: 10 }}
                  />
                  <Skeleton.Input active style={{ width: "80%" }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTableSkeleton = () => (
    <div
      className="bg-white rounded shadow-sm"
      style={{
        border: "1px solid #f0f0f0",
        padding: "20px",
      }}
    >
      {[1, 2, 3, 4, 5].map((row) => (
        <div
          key={row}
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 2fr 1fr 1fr 1fr ",
            gap: 12,
            marginBottom: 14,
          }}
        >
          {[1, 2, 3, 4, 5].map((col) => (
            <Skeleton.Input
              key={col}
              active
              block
              style={{ width: "100%" }}
            />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ padding: "25px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 30,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ fontWeight: 600, margin: 0 }}>
          Carman {selectedYear === "all" ? "" : selectedYear} Marketing
          Dashboard
        </h2>

        <div style={{ display: "flex", gap: 10 }}>
          <Select
            value={selectedYear}
            style={{ width: 140 }}
            options={years}
            onChange={(value) => {
              setSelectedYear(value);
              if (value === "all") {
                setSelectedMonth("all");
              } else if (selectedMonth === "all") {
                setSelectedMonth(currentMonth);
              }
            }}
          />

          <Select
            value={selectedMonth}
            style={{ width: 160 }}
            options={months}
            onChange={(value) => setSelectedMonth(value)}
          />
        </div>
      </div>

      {campaignInsightsLoading ? (
        renderStatSkeletons()
      ) : (
        <div className="row mb-4 g-3">
          <div className="col-md-3">
            <div className="bg-white p-4 rounded shadow-sm">
              <small className="text-muted">Total Spend</small>
              <h4>{displayCurrency(totalSpend)}</h4>
            </div>
          </div>

          <div className="col-md-3">
            <div className="bg-white p-4 rounded shadow-sm">
              <small className="text-muted">Total Clicks</small>
              <h4>{displayNumber(totalClicks)}</h4>
            </div>
          </div>

          <div className="col-md-3">
            <div className="bg-white p-4 rounded shadow-sm">
              <small className="text-muted">WhatsApp Clicks</small>
              <h4>{displayNumber(totalWhatsappClicks)}</h4>
            </div>
          </div>

          <div className="col-md-3">
            <div className="bg-white p-4 rounded shadow-sm">
              <small className="text-muted">Impressions</small>
              <h4>{displayNumber(totalImpressions)}</h4>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 15,
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <h3 style={{ margin: 0 }}>Active Campaigns</h3>

        {Array.isArray(activeCampaigns) && activeCampaigns.length > 0 && (
          <span
            style={{
              background: "#fff7e6",
              color: "#d46b08",
              border: "1px solid #ffd591",
              borderRadius: "999px",
              padding: "4px 12px",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {activeCampaigns.length} Running
          </span>
        )}
      </div>

      {activeCampaignsLoading ? (
        renderCampaignSkeletons()
      ) : Array.isArray(activeCampaigns) && activeCampaigns.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 12,
            marginBottom: 20,
          }}
        >
          {activeCampaigns.map((campaign) => (
            <div
              key={campaign.id}
              className="bg-white rounded shadow-sm"
              style={{
                width: "100%",
                border: "1px solid #f0f0f0",
                padding: "18px 20px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 20,
                flexWrap: "wrap",
              }}
            >
              <div style={{ minWidth: 240, flex: "1 1 260px" }}>
                <h5
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    color: "#1f1f1f",
                    lineHeight: 1.4,
                  }}
                >
                  {campaign.name || "Unnamed Campaign"}
                </h5>
                <small style={{ color: "#8c8c8c" }}>
                  Campaign ID: {campaign.id}
                </small>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  gap: 12,
                  flexWrap: "wrap",
                  flex: "3 1 700px",
                }}
              >
                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    Objective
                  </small>
                  <div style={{ fontWeight: 600, color: "#262626" }}>
                    {formatObjective(campaign.objective)}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    Running Ad Sets
                  </small>
                  <div style={{ fontWeight: 600, color: "#262626" }}>
                    {campaign.running_adsets ?? 0}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    Start Date
                  </small>
                  <div style={{ fontWeight: 600, color: "#262626" }}>
                    {formatDate(campaign.schedule_start)}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    End Date
                  </small>
                  <div style={{ fontWeight: 600, color: "#262626" }}>
                    {campaign.schedule_end
                      ? formatDate(campaign.schedule_end)
                      : "Ongoing"}
                  </div>
                </div>

                <div
                  style={{
                    background: "#fafafa",
                    borderRadius: 8,
                    padding: "10px 14px",
                    minWidth: 140,
                    flex: "1 1 140px",
                  }}
                >
                  <small style={{ color: "#8c8c8c", display: "block" }}>
                    Campaign Status
                  </small>
                  <div
                    style={{
                      color:
                        campaign.status === "ACTIVE" ? "#389e0d" : "#8c8c8c",
                      fontWeight: 600,
                    }}
                  >
                    {campaign.status || "—"}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="bg-white rounded shadow-sm"
          style={{
            border: "1px solid #f0f0f0",
            padding: "30px 20px",
            marginBottom: 20,
          }}
        >
          <Empty description="No active campaigns found" />
        </div>
      )}

      <h3 style={{ marginTop: 30 }}>Campaign Insights</h3>

      {campaignInsightsLoading ? (
        renderTableSkeleton()
      ) : (
        <Table
          columns={columns}
          dataSource={dataSource}
          bordered
          pagination={{ pageSize: 10 }}
        />
      )}
    </div>
  );
};

export default Dashboard;